import * as Yup from 'yup'
import { FormikErrors, yupToFormErrors } from 'formik'
import { hasHtmlText } from '../../../../../../utils/sanitizeRichText'
import { ECONOMIC_VIABILITY_TABS, STEP_TABS } from './_models'

export const OTHER_REGULATORY_APPROVAL_MAX_LENGTH = 255

export const maxCharsMessage = (label: string, maxLength: number): string =>
    `${label} must be ${maxLength} characters or less`

const requiredString = (label: string) =>
    Yup.string()
        .trim()
        .required()
        .label(label)

// An empty number input ('') is "not entered", not a type error.
const emptyAsUndefined = (value: unknown, original: unknown) => (original === '' ? undefined : value)

const requiredNumber = (label: string) =>
    Yup.number()
        .transform(emptyAsUndefined)
        .typeError(`${label} must be a number`)
        .required()
        .label(label)

const requiredArray = (label: string, min = 1) =>
    Yup.array()
        .of(Yup.string())
        .min(min, `At least ${min} ${label} must be selected`)
        .required()

// Editor HTML that must hold text, not just empty tags.
const requiredRichText = (label: string) =>
    Yup.string()
        .test('has-text', `${label} is a required field`, hasHtmlText)
        .label(label)

// Tab-wise steps validate only the open tab; a tab with no required fields has no schema.
export const stepTabSchemas: Partial<Record<string, Record<number, Yup.AnyObjectSchema>>> = {
    // The write-ups are required for the gate; the SG1 & SG2 summaries are optional.
    economic_and_commercial_viability: {
        [ECONOMIC_VIABILITY_TABS.value_for_money]: Yup.object({
            value_for_money_assessment: requiredRichText('Value-for-Money Assessment — Analysis Write-Up'),
        }),
        [ECONOMIC_VIABILITY_TABS.benefit_analysis]: Yup.object({
            benefit_analysis: requiredRichText('Benefit Analysis — Analysis Write-Up'),
        }),
        [ECONOMIC_VIABILITY_TABS.risk_evaluation]: Yup.object({
            risk_evaluation_and_contingent_liability: requiredRichText('Risk Evaluation & Contingent Liability — Analysis Write-Up'),
        }),
        [ECONOMIC_VIABILITY_TABS.economic_multipliers]: Yup.object({
            economic_multipliers_and_macroeconomic_benefits: requiredRichText('Economic Multipliers & Macroeconomic Benefits — Analysis Write-Up'),
        }),
        [ECONOMIC_VIABILITY_TABS.economies_of_scale]: Yup.object({
            economies_of_scale: requiredRichText('Economies of Scale — Analysis Write-Up'),
        }),
        [ECONOMIC_VIABILITY_TABS.cost_effectiveness]: Yup.object({
            cost_effectiveness_assessment: requiredRichText('Cost-Effectiveness Analysis — Analysis Write-Up'),
        }),
        [ECONOMIC_VIABILITY_TABS.social_economic]: Yup.object({
            social_economic_assessment: requiredRichText('Social & Economic Analysis — Analysis Write-Up'),
        }),
    },
}

// The first tab of a tab-wise step, in display order, that fails validation, with its errors.
export async function findIncompleteStepTab(stepKey: string, values: object): Promise<{ tabId: number; title: string; errors: FormikErrors<object> } | null> {
    for (const { tabId, title } of STEP_TABS[stepKey] ?? []) {
        const schema = stepTabSchemas[stepKey]?.[tabId]
        if (!schema) continue
        try {
            await schema.validate(values, { abortEarly: false })
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                return { tabId, title, errors: yupToFormErrors(error) }
            }
            throw error
        }
    }
    return null
}

export const prefeasibilityStageSchema = {

    project_status_and_readiness: Yup.object({
        current_development_phase: requiredString('Current Development Phase'),
        readiness_for_procurement: requiredString('Readiness for Procurement'),
        regulatory_approvals_status: requiredArray('Regulatory Approvals Status'),
        other_regulatory_approval: Yup.string()
            .trim()
            .max(
                OTHER_REGULATORY_APPROVAL_MAX_LENGTH,
                maxCharsMessage('Other regulatory approval', OTHER_REGULATORY_APPROVAL_MAX_LENGTH)
            ),
        stakeholder_engagement_status: requiredString('Stakeholder Engagement Status'),
        procurement_readiness: requiredString('Procurement Readiness'),
        regulatory_readiness: requiredString('Regulatory Readiness'),
        stakeholder_readiness: requiredString('Stakeholder Readiness'),
    }),

    implementation_readiness: Yup.object({

        imple_timeline_planning_start: Yup.date()
            .required("Planning start is required"),

        imple_timeline_planning_end: Yup.date()
            .required("Planning end is required")
            .min(
                Yup.ref("imple_timeline_planning_start"),
                "Planning end must be after planning start"
            ),

        imple_timeline_procurement_start: Yup.date()
            .required("Procurement start is required")
            .min(
                Yup.ref("imple_timeline_planning_end"),
                "Procurement start must not be before planning end"
            ),

        imple_timeline_procurement_end: Yup.date()
            .required("Procurement end is required")
            .min(
                Yup.ref("imple_timeline_procurement_start"),
                "Procurement end must be after procurement start"
            ),

        imple_timeline_delivery_start: Yup.date()
            .required("Delivery start is required")
            .min(
                Yup.ref("imple_timeline_procurement_end"),
                "Delivery start must not be before procurement end"
            ),

        imple_timeline_delivery_end: Yup.date()
            .required("Delivery end is required")
            .min(
                Yup.ref("imple_timeline_delivery_start"),
                "Delivery end must be after delivery start"
            ),

        imple_timeline_operational_start: Yup.date()
            .required("Operational start is required")
            .min(
                Yup.ref("imple_timeline_delivery_end"),
                "Operational start must not be before delivery end"
            ),

        imple_timeline_operational_end: Yup.date()
            .required("Operational end is required")
            .min(
                Yup.ref("imple_timeline_operational_start"),
                "Operational end must be after operational start"
            ),

        key_constraints: requiredArray('Constraint', 1),

        delivery_model_confidence_score: requiredNumber('Delivery Model Confidence Score'),
    }),

    stakeholder_and_communication_readiness: Yup.object({
        investor_engagement_status: requiredString('Investor Engagement Status'),
        community_participation_status: requiredString('Community Participation Status'),
        government_coordination_level: requiredString('Government Coordination Level'),
    }),

    risk_and_quality_gate: Yup.object({
        panoptic_evaluation_score: Yup.number()
            .transform(emptyAsUndefined)
            .typeError('Panoptic Evaluation Score must be a number')
            .integer('Panoptic Evaluation Score must be a whole number')
            .min(0, 'Panoptic Evaluation Score cannot be less than 0')
            .max(100, 'Panoptic Evaluation Score cannot exceed 100')
            .notRequired(),
    }),
    // Validated one tab at a time (stepTabSchemas).
    economic_and_commercial_viability: Yup.object({}),

    options_identification_and_screening: Yup.object({
        justification_of_option: Yup.object()
            .test(
            "min-3-justifications",
            "At least 3 justifications are required",
            (value) => {
                if (!value) {
                  return false;
                }
                return Object.values(value).length >= 3;
            }
        ),
    }),

    // Read-only view of the Stage 2 values; nothing is saved here.
    demand_and_needs_validation: Yup.object({}),

    cba_cea_intelligence: Yup.object({
        capital_cost: requiredNumber('Capital Cost'),
        annual_operating_cost: requiredNumber('Annual Operating Cost'),
        economic_benefits: requiredNumber('Economic Benefits'),
        social_benefits: requiredNumber('Social Benefits'),
        residual_value: requiredNumber('Residual Value'),
        discount_rate: requiredNumber('Discount Rate').min(0, 'Discount Rate must be between 0 and 100').max(100, 'Discount Rate must be between 0 and 100'),
        analysis_period_years: requiredNumber('Analysis Period Years')
            .integer('Analysis Period Years must be a whole number')
            .min(1, 'Analysis Period Years must be at least 1'),
    }),

    risk_and_sensitivity_analysis: Yup.object({
        capex_scenario: requiredNumber('Capex Scenario'),
        demand_scenario: requiredNumber('Demand Scenario'),
        inflation_scenario: requiredNumber('Inflation Scenario').min(0, 'Inflation Scenario must be between 0 and 100').max(100, 'Inflation Scenario must be between 0 and 100'),
        revenue_scenario: requiredNumber('Revenue Scenario'),
        schedule_delay_months: requiredNumber('Schedule Delay Months')
    }),

    documents: Yup.object({}),

}

export type IPrefeasibilityStageSchema =
    keyof typeof prefeasibilityStageSchema